// features/auth/subscription/hooks/useSubscriptionIntent.ts
import { useQuery } from "@tanstack/react-query";

import type { SubscriptionApiData } from "@/features/auth/subscription/components/SubscriptionType.ts";

import {
  createSubscriptionIntent,
  parseSubscriptionSearchParams,
} from "@/features/auth/subscription/hooks/subscriptionIntentApi.ts";

const MIN_LOADING_MS = 600;

interface UseSubscriptionIntentReturn {
  data: null | SubscriptionApiData | undefined;
  error: null | string;
  isLoading: boolean;
}

export function useSubscriptionIntent(): UseSubscriptionIntentReturn {
  const params = parseSubscriptionSearchParams();

  const hasRequiredParams = Boolean(
    params.stripe_price_id || params.product_id,
  );

  const query = useQuery({
    enabled: hasRequiredParams,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const startTime = Date.now();

      try {
        const data = await createSubscriptionIntent(params, signal);

        // Ensure minimum loading time for better UX
        const elapsed = Date.now() - startTime;
        const remaining = MIN_LOADING_MS - elapsed;

        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }

        return data;
      } catch (error) {
        // Still respect minimum loading time on error
        const elapsed = Date.now() - startTime;
        const remaining = MIN_LOADING_MS - elapsed;

        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        throw error;
      }
    },
    queryKey: ["subscription-intent", params],
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });

  return {
    data: query.data,
    error:
      !hasRequiredParams || query.isError
        ? "This link has a problem. Please check and try again."
        : null,
    isLoading: query.isPending,
  };
}
